/**
 * Landmark processing — convert MediaPipe normalized landmarks to pixel space
 * and sample mouth subsets for smile region work.
 */

import {
  INNER_LIP_INDICES,
  OUTER_LIP_INDICES,
  MOUTH_LANDMARK_INDICES,
} from "./constants";
import type { NormalizedLandmark, Point2D } from "./types";

export function toPixelCoords(
  landmarks: NormalizedLandmark[],
  imageWidth: number,
  imageHeight: number
): Point2D[] {
  return landmarks.map((lm) => ({
    x: lm.x * imageWidth,
    y: lm.y * imageHeight,
  }));
}

export function pickIndices(
  points: Point2D[],
  indices: readonly number[]
): Point2D[] {
  const out: Point2D[] = [];
  for (const i of indices) {
    if (points[i]) out.push({ ...points[i] });
  }
  return out;
}

export function getOuterLipPoints(pixelLandmarks: Point2D[]): Point2D[] {
  return pickIndices(pixelLandmarks, OUTER_LIP_INDICES);
}

export function getInnerLipPoints(pixelLandmarks: Point2D[]): Point2D[] {
  return pickIndices(pixelLandmarks, INNER_LIP_INDICES);
}

export function getMouthPoints(pixelLandmarks: Point2D[]): Point2D[] {
  return pickIndices(pixelLandmarks, MOUTH_LANDMARK_INDICES);
}

/** Order lip ring so polygon edges don't cross (sort by angle around centroid). */
export function orderPolygonClockwise(points: Point2D[]): Point2D[] {
  if (points.length < 3) return [...points];

  const cx = points.reduce((s, p) => s + p.x, 0) / points.length;
  const cy = points.reduce((s, p) => s + p.y, 0) / points.length;

  return [...points].sort(
    (a, b) =>
      Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx)
  );
}

export function remapPointsToLocal(
  points: Point2D[],
  originX: number,
  originY: number
): Point2D[] {
  return points.map((p) => ({ x: p.x - originX, y: p.y - originY }));
}
